import * as React from "react";
import clsx from "clsx";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Box, Plus, Cpu, Play, ChevronDown, Clock, Pencil, Trash2, Settings, MapPin, Image as ImageIcon, Database, Activity, Shield, Pause, BarChart3, Bell, Users, AlertTriangle, X, Sparkles } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Card } from "../../../ui/components/Card";
import { ListView } from "../../../ui/components/ListView";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import { useBooleanFeatureFlag } from "../../lib/featureFlags/hooks";
import type { InsightEvidenceWindow } from "../../lib/api/insights";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import * as productsApi from "../../lib/api/products";
import * as productAccessApi from "../../lib/api/product-access";
import type { Product, ProductStatus, TelemetrySnapshot, DataCatalog, CodeMatch, Component } from "../../lib/api/products";
import { ComponentFormModal } from "./ComponentFormModal";
import { ComponentViewModal } from "./ComponentViewModal";
import { ProductAccessSection } from "./ProductAccessSection";
import { ProductSubscriptionsModal } from "./ProductSubscriptionsModal";
import { ProductSubscribersSection } from "./ProductSubscribersSection";
import { ProductAlertsSection, DEVICE_OFFLINE_MESSAGE } from "./ProductAlertsSection";
import { SimulationAlertControls } from "./SimulationAlertControls";
import { ActiveAlertsCountBadge } from "./ActiveAlertsCountBadge";
import { useProductActiveAlerts } from "./useProductActiveAlerts";
import * as simAlertStyles from "./SimulationAlertControls.css";
import {
  ProductAccessTierBadge,
  formatProductAccessTooltip,
} from "./ProductAccessTierBadge";
import { RequestProductAccessForm } from "./RequestProductAccessForm";
import { RequestProductAccessModal } from "./RequestProductAccessModal";
import { OrgThumbnail } from "../../../ui/components/OrgThumbnail";
import { ProApiClientError } from "../../lib/api/pro-api.client";
import type {
  EffectiveProductAccess,
  ProductAccessTier,
} from "../../lib/api/product-access";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { ListViewComponentCard } from "../../../ui/components/ListView/components/ListViewComponentCard";
import { parseTelemetryDate, parseTelemetryTimeMs } from "../../../ui/components/ListView/chart/parseTelemetryTime";
import { SimulationControlCard } from "./SimulationControlCard";
import { ProductImageGallery } from "./ProductImageGallery";
import { formatValue } from "../../lib/units";
import { useWebSocket } from "../../hooks/useWebSocket";
import { fetchProductImages } from "../../lib/profile-avatar-upload";
import * as s from "./ProductPage.css";
import { DeviceHealthIndicator } from "../../../ui/components/DeviceHealthIndicator";
import { maxIsoTimestamp, resolveLastSignalAt } from "../../../ui/components/DeviceHealthIndicator/resolveLastSignalAt";
import { RealtimeIndicator } from "../../../ui/components/RealtimeIndicator";
import { HistoricalDataIndicator } from "../../../ui/components/HistoricalDataIndicator";
import type { HistoricalDataStatus } from "../../../ui/components/HistoricalDataIndicator";
import {
  initialHistoryKeysForProductLine,
  pickDefaultHistoryKeys,
} from "./historyDefaultKeys";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import type { ContextAction } from "../../../ui/components/ContextActions";
import { SmartLinkSyncStatusBadge } from "../../../ui/components/SmartLinkSyncStatusBadge";
import { ProductInsightsSection } from "./insights/ProductInsightsSection";
import { ComingSoonCard } from "../../../ui/components/ComingSoonCard";

// ─── Section IDs ───────────────────────────────────────────────────────────

const SECTION_IDS = {
  simulation: (productId: string) => `product-simulation-${productId}`,
  insights: (productId: string) => `product-insights-${productId}`,
  system: (productId: string) => `product-system-${productId}`,
  images: (productId: string) => `product-images-${productId}`,
  location: (productId: string) => `product-location-${productId}`,
  components: (productId: string) => `product-components-${productId}`,
  access: (productId: string) => `product-access-${productId}`,
  subscribers: (productId: string) => `product-subscribers-${productId}`,
  realtime: (productId: string) => `product-realtime-${productId}`,
  historical: (productId: string) => `product-historical-${productId}`,
  alerts: (productId: string) => `product-alerts-${productId}`,
} as const;

// ─── Domain resolution ─────────────────────────────────────────────────────

const SANCTUARY_PRODUCT_LINES = ['sanctuary', 'peak', 'sanctuary_1', 'sanctuary_2', 'sanctuary_3', 'vault'];

function resolveProductDomain(productLine?: string | null): string | null {
  if (!productLine) return null;
  return SANCTUARY_PRODUCT_LINES.includes(productLine) ? 'sanctuary' : 'powersave';
}

// ─── Telemetry display helpers ─────────────────────────────────────────────

/**
 * Simulation / enrichment sometimes nests another measure object under `raw` and
 * `value`. Flatten to scalars so table + chart both get numeric points.
 */
function unwrapTelemetryScalar(m: Record<string, unknown> | null | undefined): {
  raw: string | number | null;
  value: string | number | null;
  unit: string;
  label: string;
} {
  if (!m || typeof m !== 'object') {
    return { raw: null, value: null, unit: '', label: '' };
  }

  const pickScalar = (v: unknown, depth = 0): string | number | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number' || typeof v === 'string') return v;
    if (typeof v === 'object' && depth < 3) {
      const o = v as Record<string, unknown>;
      if ('value' in o) {
        const fromValue = pickScalar(o.value, depth + 1);
        if (fromValue !== null) return fromValue;
      }
      if ('raw' in o) return pickScalar(o.raw, depth + 1);
    }
    return null;
  };

  const nested =
    (m.value && typeof m.value === 'object' && !Array.isArray(m.value)
      ? (m.value as Record<string, unknown>)
      : null) ??
    (m.raw && typeof m.raw === 'object' && !Array.isArray(m.raw)
      ? (m.raw as Record<string, unknown>)
      : null);

  const unit =
    (typeof m.unit === 'string' && m.unit) ||
    (typeof nested?.unit === 'string' && nested.unit) ||
    '';
  const label =
    (typeof m.label === 'string' && m.label) ||
    (typeof nested?.label === 'string' && nested.label) ||
    '';

  return {
    raw: pickScalar(m.raw),
    value: pickScalar(m.value) ?? pickScalar(m.raw),
    unit,
    label,
  };
}

function formatMeasureDisplay(measure: Record<string, unknown>, catalogKey?: import("../../lib/api/products").DataCatalogKey | null): string {
  const m = measure as any;
  if (m.description != null) return String(m.description);
  if (m.codes != null) {
    const codes = m.codes as any[];
    if (codes.length === 0) return '—';
    return codes.map((c: any) => c.label ?? c.code).join(', ');
  }
  if (typeof m.value === 'number') {
    const str = formatValue(m.value, catalogKey?.precision ?? null);
    return m.unit ? `${str} ${m.unit}` : str;
  }
  const str = String(m.value ?? '');
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) return parsed.length === 0 ? '—' : parsed.join(', ');
  } catch { /* not JSON */ }
  return str || '—';
}

/** Returns the formatted value portion only (no unit). */
function formatMeasureValue(measure: Record<string, unknown>, catalogKey?: import("../../lib/api/products").DataCatalogKey | null): string {
  const m = measure as any;
  if (m.description != null) return String(m.description);
  if (m.codes != null) {
    const codes = m.codes as any[];
    if (codes.length === 0) return '—';
    return codes.map((c: any) => c.label ?? c.code).join(', ');
  }
  if (typeof m.value === 'number') {
    return formatValue(m.value, catalogKey?.precision ?? null);
  }
  const str = String(m.value ?? '');
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) return parsed.length === 0 ? '—' : parsed.join(', ');
  } catch { /* not JSON */ }
  return str || '—';
}

function formatRawDisplay(measure: Record<string, unknown>): string {
  const m = measure as any;
  if (m.raw === null || m.raw === undefined) return '—';
  const str = String(m.raw);
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) return parsed.length === 0 ? '—' : (parsed as unknown[]).join(', ');
  } catch { /* not JSON */ }
  return str;
}

interface DatapointRow {
  id: string;
  key: string;
  label: string;
}

const DURATION_OPTIONS: { label: string; short: string; ms: number }[] = [
  { label: "1 minute", short: "1m", ms: 60_000 },
  { label: "1 hour", short: "1hr", ms: 3_600_000 },
  { label: "12 hours", short: "12hr", ms: 43_200_000 },
  { label: "24 hours", short: "24hr", ms: 86_400_000 },
];

function StartSimulationControl({
  token,
  productId,
  onStarted,
  selectedDuration,
  onDurationChange,
}: {
  token: string | null;
  productId: string;
  onStarted: (updates: Partial<Product>) => void;
  selectedDuration: (typeof DURATION_OPTIONS)[number];
  onDurationChange: (opt: (typeof DURATION_OPTIONS)[number]) => void;
}) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [starting, setStarting] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      <div
        style={{
          display: "flex",
          borderRadius: 6,
          overflow: "hidden",
          border: "1px solid rgba(0,0,0,0.15)",
          height: 34,
          opacity: starting ? 0.5 : 1,
          pointerEvents: starting ? "none" : "auto",
        }}
      >
        <button
          type="button"
          disabled={starting}
          onClick={async () => {
            if (!token) return;
            setStarting(true);
            try {
              const result = await productsApi.startProductSimulation(
                token,
                productId,
                selectedDuration.ms,
              );
              onStarted({
                isSimulating: result.isSimulating,
                simulationExpAt: result.simulationExpAt,
                simulationStartedAt: result.simulationStartedAt,
              });
            } catch {
              /* ignore */
            } finally {
              setStarting(false);
            }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "0 12px",
            height: "100%",
            background: "#FFCF51",
            border: "none",
            borderRight: "1px solid rgba(0,0,0,0.15)",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            color: "#111827",
            whiteSpace: "nowrap",
          }}
        >
          {starting ? (
            <span
              style={{
                display: "inline-block",
                width: 14,
                height: 14,
                border: "2px solid #111827",
                borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "spin 0.6s linear infinite",
              }}
            />
          ) : (
            <Play size={13} strokeWidth={2.5} />
          )}
          Start simulation
        </button>
        <button
          type="button"
          disabled={starting}
          onClick={() => setDropdownOpen((p) => !p)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "0 8px",
            height: "100%",
            background: "#FFCF51",
            border: "none",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            color: "#111827",
            whiteSpace: "nowrap",
          }}
        >
          {selectedDuration.short}
          <ChevronDown size={13} strokeWidth={2.5} />
        </button>
      </div>
      {dropdownOpen && (
        <div className={s.catalogMenuDropdown} style={{ minWidth: 140 }}>
          {DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.ms}
              type="button"
              onClick={() => {
                onDurationChange(opt);
                setDropdownOpen(false);
              }}
              className={s.catalogMenuItem}
              style={{ fontWeight: opt.ms === selectedDuration.ms ? 600 : 400 }}
            >
              <Clock size={13} />
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function Field({ label, value }: { label: string; value?: string | null }) {
  const { t } = useI18n();
  return (
    <div className={s.fieldGroup}>
      <span className={s.fieldLabel}>{label}</span>
      {value ? (
        <span className={s.fieldValue}>{value}</span>
      ) : (
        <span className={s.fieldValueMuted}>{t("pro.products.notSet")}</span>
      )}
    </div>
  );
}

const STATUS_STYLES: Record<ProductStatus, { bg: string; color: string; dot: string }> = {
  active:        { bg: "#F0FDF4", color: "#16A34A", dot: "#16A34A" },
  inactive:      { bg: "#F3F4F6", color: "#6B7280", dot: "#9CA3AF" },
  decommissioned:{ bg: "#FEF2F2", color: "#991B1B", dot: "#EF4444" },
};

function StatusBadge({ status }: { status: string }) {
  const styles = STATUS_STYLES[status as ProductStatus] ?? STATUS_STYLES.active;
  const label = status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
  const title =
    status === "active"
      ? "Product is active and available"
      : status === "inactive"
        ? "Product is inactive"
        : status === "decommissioned"
          ? "Product has been decommissioned"
          : `Product status: ${label}`;
  return (
    <span
      className={s.statusBadge}
      title={title}
      style={{ background: styles.bg, color: styles.color }}
    >
      <span
        style={{
          width: 6, height: 6, borderRadius: "50%",
          background: styles.dot, flexShrink: 0,
        }}
      />
      {label}
    </span>
  );
}

function structuredAccessTier(
  tier: EffectiveProductAccess["tier"] | null | undefined,
): ProductAccessTier | null {
  if (tier === "view" || tier === "edit" || tier === "full") return tier;
  return null;
}

function preferredRequestTier(
  current: ProductAccessTier | null | undefined,
): ProductAccessTier {
  if (current === "view") return "edit";
  if (current === "edit") return "full";
  return "view";
}

// Helper: Simulation status display (extracted from SimulationControlCard)
function SimulationStatusInner({
  product,
  activeAlerts = [],
  onClearAlert,
  onAddAlert,
  clearBusyCode,
  canEdit,
  children,
}: {
  product: Product;
  activeAlerts?: Array<{
    code: string;
    name: string;
    type: string;
    severity: number;
  }>;
  onClearAlert?: (code: string) => void;
  onAddAlert?: () => void;
  clearBusyCode?: string | null;
  canEdit?: boolean;
  children?: React.ReactNode;
}) {
  const { t, tx } = useI18n();
  
  // Countdown/elapsed hooks (from SimulationControlCard)
  const formatDuration = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const useCountdown = (targetIso: string | null | undefined): string => {
    const [display, setDisplay] = React.useState("");
    React.useEffect(() => {
      if (!targetIso) { setDisplay(""); return; }
      const update = () => {
        const remaining = new Date(targetIso).getTime() - Date.now();
        if (remaining <= 0) { setDisplay("Expiring…"); return; }
        setDisplay(formatDuration(remaining));
      };
      update();
      const id = setInterval(update, 1000);
      return () => clearInterval(id);
    }, [targetIso]);
    return display;
  };

  const useElapsed = (startIso: string | null | undefined): string => {
    const [display, setDisplay] = React.useState("");
    React.useEffect(() => {
      if (!startIso) { setDisplay(""); return; }
      const update = () => {
        const elapsed = Date.now() - new Date(startIso).getTime();
        if (elapsed < 0) { setDisplay("0s"); return; }
        setDisplay(formatDuration(elapsed));
      };
      update();
      const id = setInterval(update, 1000);
      return () => clearInterval(id);
    }, [startIso]);
    return display;
  };

  const useRemainingFraction = (
    startIso: string | null | undefined,
    expIso: string | null | undefined,
  ): number => {
    const [fraction, setFraction] = React.useState(1);
    React.useEffect(() => {
      if (!startIso || !expIso) { setFraction(1); return; }
      const total = new Date(expIso).getTime() - new Date(startIso).getTime();
      if (total <= 0) { setFraction(0); return; }
      const update = () => {
        const remaining = new Date(expIso).getTime() - Date.now();
        setFraction(Math.max(0, Math.min(1, remaining / total)));
      };
      update();
      const id = setInterval(update, 1000);
      return () => clearInterval(id);
    }, [startIso, expIso]);
    return fraction;
  };

  const expiryCountdown = useCountdown(product.isSimulating ? product.simulationExpAt : null);
  const elapsed = useElapsed(product.isSimulating ? product.simulationStartedAt : null);
  const remainingFraction = useRemainingFraction(
    product.isSimulating ? product.simulationStartedAt : null,
    product.isSimulating ? product.simulationExpAt : null,
  );

  const RING_SIZE = 44;
  const RING_STROKE = 4;
  const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
  const RING_CIRC = 2 * Math.PI * RING_RADIUS;
  const offset = RING_CIRC * (1 - remainingFraction);

  const statusBoxRef = React.useRef<HTMLDivElement>(null);
  const badgeRef = React.useRef<HTMLDivElement>(null);
  const timingRef = React.useRef<HTMLDivElement>(null);
  const alertStackRef = React.useRef<HTMLDivElement>(null);
  const addAlertBtnRef = React.useRef<HTMLButtonElement>(null);
  const [stacked, setStacked] = React.useState(false);

  React.useLayoutEffect(() => {
    if (!product.isSimulating) return;
    const box = statusBoxRef.current;
    if (!box) return;

    const MAIN_GAP = 20;
    const ALERT_GAP = 10;
    const H_PAD = 32; // statusBoxActive padding L+R

    const check = () => {
      const available = Math.max(0, box.clientWidth - H_PAD);
      // Measure content intrinsic widths — stacked rows use width:100% wrappers
      // which would otherwise lock us into stacked forever.
      const badgeW =
        (badgeRef.current?.firstElementChild as HTMLElement | undefined)?.offsetWidth ?? 0;
      const timingEl = timingRef.current;
      const timingW = timingEl
        ? Array.from(timingEl.children).reduce((sum, child, i) => {
            return sum + (child as HTMLElement).offsetWidth + (i > 0 ? 16 : 0);
          }, 0)
        : 0;
      // Sum chip widths so wrapping inside the stack doesn't under-report needed row width.
      const stackEl = alertStackRef.current;
      const stackW = stackEl
        ? Array.from(stackEl.children).reduce((sum, child, i) => {
            return sum + (child as HTMLElement).offsetWidth + (i > 0 ? 8 : 0);
          }, 0)
        : 0;
      // Always use the square (row) button size so the stacked text CTA doesn't bias the check.
      const addW = addAlertBtnRef.current ? 56 : 0;
      const alertParts = [stackW, addW].filter((w) => w > 0);
      const alertsW =
        alertParts.reduce((sum, w) => sum + w, 0) +
        Math.max(0, alertParts.length - 1) * ALERT_GAP;
      const sections = [badgeW, timingW, alertsW].filter((w) => w > 0);
      const need =
        sections.reduce((sum, w) => sum + w, 0) +
        Math.max(0, sections.length - 1) * MAIN_GAP;
      setStacked(need > available + 1);
    };

    check();
    const ro = new ResizeObserver(check);
    ro.observe(box);
    return () => ro.disconnect();
  }, [product.isSimulating, activeAlerts.length, onAddAlert, canEdit]);

  if (product.isSimulating) {
    return (
      <div ref={statusBoxRef} className={simAlertStyles.statusBoxActive}>
        <div
          className={simAlertStyles.statusLayout}
          data-stacked={stacked ? "true" : "false"}
        >
          <div ref={badgeRef} className={simAlertStyles.statusBadgeRow}>
            <span className={simAlertStyles.activeBadge}>
              <Play size={10} strokeWidth={2.5} /> Active
            </span>
          </div>
          <div ref={timingRef} className={simAlertStyles.statusTimingRow}>
            <div>
              <div className={s.fieldLabel}>{tx("pro.products.runningFor")}</div>
              <div className={s.fieldValue} style={{ fontVariantNumeric: "tabular-nums" }}>{elapsed || "—"}</div>
            </div>
            <svg
              width={RING_SIZE}
              height={RING_SIZE}
              viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
              style={{ flexShrink: 0, transform: "rotate(-90deg)" }}
              aria-hidden="true"
            >
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                stroke="currentColor"
                strokeWidth={RING_STROKE}
                opacity={0.15}
              />
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                stroke="#16A34A"
                strokeWidth={RING_STROKE}
                strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={offset}
                style={{ transition: "stroke-dashoffset 1s linear" }}
              />
            </svg>
            <div>
              <div className={s.fieldLabel}>{tx("pro.products.expiresIn")}</div>
              <div className={s.fieldValue} style={{ fontVariantNumeric: "tabular-nums" }}>{expiryCountdown || "—"}</div>
            </div>
          </div>
          <div className={simAlertStyles.alertStackArea}>
            <div
              ref={alertStackRef}
              className={simAlertStyles.alertStack}
              aria-label={t("pro.products.alerts.activeSimulatedAria")}
            >
              {activeAlerts.map((alert) => (
                <div key={alert.code} className={simAlertStyles.alertStackItem}>
                  <div
                    className={simAlertStyles.activeAlertPulse}
                    data-severity={String(Math.min(5, Math.max(1, alert.severity || 1)))}
                    title={[
                      alert.name,
                      `Type: ${alert.type === "fault" ? "Fault" : "Alarm"}`,
                      `Severity: ${
                        ({ 1: "Suggestion", 2: "Low", 3: "Medium", 4: "High", 5: "Critical" } as Record<number, string>)[
                          alert.severity
                        ] ?? String(alert.severity)
                      }`,
                    ].join("\n")}
                  >
                    <span className={simAlertStyles.activeAlertPulseRing} aria-hidden />
                    {alert.type === "fault" ? (
                      <AlertTriangle size={22} strokeWidth={2.25} aria-hidden />
                    ) : (
                      <Bell size={22} strokeWidth={2.25} aria-hidden />
                    )}
                    <span className={simAlertStyles.activeAlertCode}>{alert.code}</span>
                    {canEdit && onClearAlert ? (
                      <button
                        type="button"
                        className={simAlertStyles.activeAlertClear}
                        aria-label={`Clear alert ${alert.code}`}
                        disabled={clearBusyCode === alert.code}
                        onClick={(e) => {
                          e.stopPropagation();
                          onClearAlert(alert.code);
                        }}
                      >
                        <X size={10} strokeWidth={2.5} />
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {onAddAlert ? (
              <button
                ref={addAlertBtnRef}
                type="button"
                className={simAlertStyles.addAlertButton}
                title={t("pro.products.alerts.simulate")}
                aria-label={t("pro.products.alerts.simulate")}
                onClick={onAddAlert}
              >
                <Plus size={14} strokeWidth={2.5} aria-hidden />
                <AlertTriangle
                  size={14}
                  strokeWidth={2.25}
                  className={simAlertStyles.addAlertButtonExtraIcon}
                  aria-hidden
                />
                <span className={simAlertStyles.addAlertButtonLabel}>{t("pro.fleet.alerts.badgeAlert")}</span>
              </button>
            ) : null}
          </div>
        </div>
        {children ? <div className={simAlertStyles.statusFooter}>{children}</div> : null}
      </div>
    );
  }

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "12px 16px", borderRadius: 8, minHeight: 68,
      border: "1px solid #3B82F6",
    }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "#1D4ED8", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 9999, padding: "2px 10px", flexShrink: 0 }}>
        <Pause size={10} strokeWidth={2.5} /> Paused
      </span>
      <Text level="body-small" style={{ color: "#9CA3AF" }}>
        Start a session to generate simulated telemetry data for this product.
      </Text>
    </div>
  );
}

export function ProductDetailPage({ productId }: { productId: string }) {
  const { t, tx } = useI18n();
  const navigate = useNavigate();
  const { accessToken, user, userPayload } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");
  const currentUserId = user?.id ?? userPayload?.sub ?? null;
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse(`product-detail-${productId}`);
  const { alerts: activeAlertSummaries } = useProductActiveAlerts(productId, token);
  const [insightsOpenCount, setInsightsOpenCount] = React.useState<number | null>(null);
  const insightsFlagEnabled = useBooleanFeatureFlag("insights.enabled");

  const [product, setProduct] = React.useState<Product | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [accessDenied, setAccessDenied] = React.useState(false);
  const [accessPending, setAccessPending] = React.useState(false);
  const [accessPendingTier, setAccessPendingTier] =
    React.useState<ProductAccessTier | null>(null);
  const [myAccess, setMyAccess] = React.useState<EffectiveProductAccess | null>(
    null,
  );
  const [canManageAccess, setCanManageAccess] = React.useState(false);
  const [accessCount, setAccessCount] = React.useState<number | null>(null);
  const [imageCount, setImageCount] = React.useState<number | null>(null);
  const [subscriberCount, setSubscriberCount] = React.useState<number | null>(null);
  const [alertCounts, setAlertCounts] = React.useState<{ active: number; recent: number } | null>(null);
  const [historyRangeLabel, setHistoryRangeLabel] = React.useState<string | null>(null);
  const [headerImageUrl, setHeaderImageUrl] = React.useState<string | null>(null);
  const [simDuration, setSimDuration] = React.useState(DURATION_OPTIONS[0]);
  const [requestingAccess, setRequestingAccess] = React.useState(false);
  const [cancellingAccessRequest, setCancellingAccessRequest] =
    React.useState(false);
  const [requestAccessError, setRequestAccessError] = React.useState<
    string | null
  >(null);
  const [requestModalOpen, setRequestModalOpen] = React.useState(false);
  const [deniedProductName, setDeniedProductName] = React.useState<string | null>(null);
  const [serviceStartedAt, setServiceStartedAt] = React.useState<number | undefined>(undefined);
  const productRef = React.useRef<Product | null>(null);
  productRef.current = product;

  React.useEffect(() => {
    if (!token) return;
    const API_BASE = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? "http://localhost:3003/api";
    fetch(`${API_BASE}/health/product-service`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data: { startedAt?: string } | null) => {
        if (data?.startedAt) setServiceStartedAt(new Date(data.startedAt).getTime());
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const applyPendingRequest = React.useCallback(
    (pending: productAccessApi.ProductAccessPendingRequest | null | undefined) => {
      setAccessPending(Boolean(pending));
      setAccessPendingTier(
        pending?.requestedTier === "view" ||
          pending?.requestedTier === "edit" ||
          pending?.requestedTier === "full"
          ? pending.requestedTier
          : pending
            ? "view"
            : null,
      );
    },
    [],
  );

  const refreshAccess = React.useCallback(
    async (options?: { isStale?: () => boolean }) => {
      if (!token) return;
      const stale = () => Boolean(options?.isStale?.());
      try {
        const check = await productAccessApi.fetchProductAccessCheck(
          token,
          productId,
        );
        if (stale()) return;

        if (!check.allowed) {
          setAccessDenied(true);
          setProduct(null);
          setMyAccess(null);
          setCanManageAccess(false);
          applyPendingRequest(check.pendingRequest);
          setDeniedProductName(check.product?.name ?? null);
          setError(null);
          setRequestModalOpen(false);
          setLoading(false);
          return;
        }

        applyPendingRequest(check.pendingRequest);
        setMyAccess(check.access ?? null);
        setCanManageAccess(Boolean(check.canManage));
        setDeniedProductName(check.product?.name ?? null);

        // Load the product before clearing accessDenied so we never render the
        // "!product" error branch during a live grant transition.
        if (!productRef.current) {
          setLoading(true);
          try {
            const p = await productsApi.getProductById(token, productId);
            if (stale()) return;
            setProduct(p);
            const access = (p as Product & { access?: { canManage?: boolean } })
              .access;
            if (access?.canManage != null) {
              setCanManageAccess(Boolean(access.canManage));
            }
            setAccessDenied(false);
            setRequestModalOpen(false);
            setError(null);
          } finally {
            if (!stale()) setLoading(false);
          }
          return;
        }

        setAccessDenied(false);
        if (check.access) {
          setCanManageAccess(
            Boolean(check.canManage || check.access.canManage),
          );
        }
        setError(null);
      } catch (err) {
        if (stale()) return;
        const isDenied =
          err instanceof ProApiClientError &&
          err.status === 403 &&
          (err.data?.code === "PRODUCT_ACCESS_DENIED" ||
            String(err.message ?? "").includes("do not have access") ||
            String(err.data?.error ?? "").includes("do not have access"));
        if (isDenied) {
          setAccessDenied(true);
          setProduct(null);
          setMyAccess(null);
          setCanManageAccess(false);
          setRequestModalOpen(false);
          setLoading(false);
          try {
            const check = await productAccessApi.fetchProductAccessCheck(
              token,
              productId,
            );
            if (stale()) return;
            applyPendingRequest(check.pendingRequest);
            setDeniedProductName(check.product?.name ?? null);
          } catch {
            /* ignore */
          }
          setError(null);
        } else if (!productRef.current) {
          setLoading(false);
          setError(
            err instanceof Error ? err.message : "Failed to load product",
          );
        }
      }
    },
    [token, productId, applyPendingRequest],
  );

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    setAccessPending(false);
    setAccessPendingTier(null);
    setMyAccess(null);
    setCanManageAccess(false);
    setProduct(null);
    setDeniedProductName(null);
    setRequestAccessError(null);
    setRequestModalOpen(false);

    void (async () => {
      await refreshAccess({ isStale: () => cancelled });
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [token, productId, refreshAccess]);

  const submitAccessRequest = React.useCallback(
    async (tier: ProductAccessTier) => {
      if (!token) return;
      setRequestingAccess(true);
      setRequestAccessError(null);
      try {
        await productAccessApi.requestProductAccess(token, productId, tier);
        setAccessPending(true);
        setAccessPendingTier(tier);
        setRequestModalOpen(false);
      } catch (e) {
        setRequestAccessError(
          getApiErrorMessage(e, "Failed to request access"),
        );
      } finally {
        setRequestingAccess(false);
      }
    },
    [token, productId],
  );

  const cancelAccessRequest = React.useCallback(async () => {
    if (!token) return;
    setCancellingAccessRequest(true);
    setRequestAccessError(null);
    try {
      await productAccessApi.cancelProductAccessRequest(token, productId);
      setAccessPending(false);
      setAccessPendingTier(null);
    } catch (e) {
      setRequestAccessError(
        getApiErrorMessage(e, "Failed to cancel request"),
      );
    } finally {
      setCancellingAccessRequest(false);
    }
  }, [token, productId]);

  // ── Telemetry ──────────────────────────────────────────────────────────────
  const [telemetry, setTelemetry] = React.useState<TelemetrySnapshot | null>(null);
  const [telemetryLoading, setTelemetryLoading] = React.useState(false);
  const [telemetryError, setTelemetryError] = React.useState<string | null>(null);
  const [telemetryOffline, setTelemetryOffline] = React.useState(false);
  const [telemetryStale, setTelemetryStale] = React.useState(false);
  const [telemetryStaleAgeMs, setTelemetryStaleAgeMs] = React.useState<number | null>(null);
  // null = product-level measures; componentId string = that component's measures
  const [telemetrySource, setTelemetrySource] = React.useState<string | null>(null);
  // Version counter per measure key — drives ListView forcedFlashIds
  const [datapointVersions, setDatapointVersions] = React.useState<Map<string, number>>(() => new Map());

  const { subscribe, unsubscribe } = useWebSocket();

  /** Bumped when product-service reports an Insights change, so the section refetches. */
  const [insightsRefreshKey, setInsightsRefreshKey] = React.useState(0);
  React.useEffect(() => {
    if (!productId) return;
    const topic = `entity:products:${productId}`;
    const handler = (msg: { payload?: unknown }) => {
      if ((msg.payload as { type?: string } | undefined)?.type === "insights.updated") {
        setInsightsRefreshKey((k) => k + 1);
      }
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [productId, subscribe, unsubscribe]);

  const loadHeaderImage = React.useCallback(() => {
    if (!token || !productId) {
      setHeaderImageUrl(null);
      return;
    }
    void fetchProductImages(token, productId).then((images) => {
      const sorted = [...images].sort((a, b) => a.sortOrder - b.sortOrder);
      const first = sorted[0];
      setHeaderImageUrl(first?.thumbUrl ?? first?.cardUrl ?? first?.url ?? null);
    });
  }, [token, productId]);

  React.useEffect(() => {
    loadHeaderImage();
  }, [loadHeaderImage]);

  React.useEffect(() => {
    if (!productId) return;
    const topic = `entity:products:${productId}`;
    subscribe([topic], loadHeaderImage);
    return () => unsubscribe([topic], loadHeaderImage);
  }, [productId, subscribe, unsubscribe, loadHeaderImage]);

  const updateTelemetry = React.useCallback((snap: TelemetrySnapshot) => {
    // Guard: offline responses have no measures — don't overwrite real telemetry with them
    if ((snap as any).offline && !snap.measures) return;
    setTelemetry((prev) => {
      const measures = snap.measures ?? {};
      if (prev) {
        const changed = Object.keys(measures).filter(
          (k) => JSON.stringify((measures[k] as {value?: unknown})?.value) !==
                  JSON.stringify((prev.measures?.[k] as {value?: unknown})?.value)
        );
        if (changed.length > 0) {
          setDatapointVersions((v) => {
            const next = new Map(v);
            for (const k of changed) next.set(k, (next.get(k) ?? 0) + 1);
            return next;
          });
        }
      }
      // Preserve heartbeat fields when a WS/latest payload omits them
      return {
        ...prev,
        ...snap,
        measures: Object.keys(measures).length > 0 ? measures : (prev?.measures ?? {}),
        lastTelemetryAt: maxIsoTimestamp(
          snap.lastTelemetryAt ?? snap.time,
          prev?.lastTelemetryAt,
        ),
        lastShadowAt: maxIsoTimestamp(snap.lastShadowAt, prev?.lastShadowAt),
        lastSignalAt: maxIsoTimestamp(snap.lastSignalAt, prev?.lastSignalAt),
      };
    });
  }, []);

  const loadTelemetry = React.useCallback((keys?: string[]) => {
    if (!token || (!product?.iotThingName && !product?.isSimulated)) return;
    // Paused simulated products have no live data — skip the fetch entirely
    if (product?.isSimulated && !product?.isSimulating) return;
    setTelemetryLoading(true);
    setTelemetryError(null);
    setTelemetryOffline(false);
    // Pass key filter when provided — only request the measures we're displaying
    productsApi.fetchTelemetryLatest(token, productId, keys?.length ? keys : undefined)
      .then((snap) => {
        if (snap._noData) {
          setTelemetryOffline(true);
          setTelemetryStale(false);
          setTelemetryStaleAgeMs(null);
          // Still keep heartbeat timestamps so DeviceHealth matches the list page
          // (shadow-only devices often return _noData for measures).
          setTelemetry((prev) => ({
            productId,
            time: prev?.time ?? null,
            measures: prev?.measures ?? {},
            components: prev?.components ?? [],
            _noData: true,
            lastTelemetryAt: maxIsoTimestamp(snap.lastTelemetryAt, prev?.lastTelemetryAt),
            lastShadowAt: maxIsoTimestamp(snap.lastShadowAt, prev?.lastShadowAt),
            lastSignalAt: maxIsoTimestamp(snap.lastSignalAt, prev?.lastSignalAt),
          }));
          return;
        }
        setTelemetryStale(snap._stale === true);
        setTelemetryStaleAgeMs(snap._staleAgeMs ?? null);
        updateTelemetry(snap);
      })
      .catch((err) => setTelemetryError(err?.message || "Failed to load telemetry"))
      .finally(() => setTelemetryLoading(false));
  }, [token, productId, product?.iotThingName, product?.isSimulated, product?.isSimulating, updateTelemetry]);

  React.useEffect(() => { loadTelemetry(); }, [loadTelemetry]);

  // ── Catalog ────────────────────────────────────────────────────────────────
  const [catalog, setCatalog] = React.useState<DataCatalog | null>(null);
  // catalogKeys is the ordered list of keys from the catalog definition.
  // Used to order the datapoint ListView and resolve labels for unlabeled measures.
  const [catalogKeys, setCatalogKeys] = React.useState<import("../../lib/api/products").DataCatalogKey[]>([]);
  const [catalogAlerts, setCatalogAlerts] = React.useState<import("../../lib/api/products").DataCatalogAlert[]>([]);

  React.useEffect(() => {
    if (!token) return;
    productsApi.fetchDataCatalogs(token).then(async (catalogs) => {
      // Mirror product-service catalog.service.ts:
      // override → productLine slug family → per-model exact match
      const PRODUCT_LINE_TO_SLUG: Record<string, string> = {
        peak: "sanctuary",
        sanctuary: "sanctuary",
        sanctuary_1: "sanctuary",
        sanctuary_2: "sanctuary",
        sanctuary_3: "sanctuary",
        vault: "sanctuary",
        cabinet: "cabinet",
        container: "container",
      };
      const line = product?.productModel?.productLine ?? null;
      const slug = line ? PRODUCT_LINE_TO_SLUG[line] : undefined;
      const match = product?.dataCatalogId
        ? (catalogs.find((c) => c.id === product.dataCatalogId && c.isActive) ?? null)
        : (slug
            ? (catalogs.find((c) => c.slug === slug && c.isActive) ?? null)
            : null)
          ?? (product?.productModelId
            ? (catalogs.find((c) => c.productModelId === product.productModelId && c.isActive) ?? null)
            : null);
      if (!match) { setCatalog(null); setCatalogKeys([]); setCatalogAlerts([]); return; }
      setCatalog(match);
      // Fetch full detail to get the keys array
      try {
        const detail = await productsApi.fetchDataCatalog(token, match.id);
        const sorted = (detail.keys ?? [])
          .filter((k) => k.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        setCatalogKeys(sorted);
        setCatalogAlerts((detail.alerts ?? []).filter((a) => a.isActive));
      } catch {
        setCatalogKeys([]);
        setCatalogAlerts([]);
      }
    }).catch(() => { setCatalog(null); setCatalogKeys([]); setCatalogAlerts([]); });
  }, [token, product?.dataCatalogId, product?.productModelId, product?.productModel?.productLine]);

  // ── WebSocket telemetry subscription ──────────────────────────────────────
  React.useEffect(() => {
    if (!product?.iotThingName && !product?.isSimulated) return;
    const topic = 'telemetry:products:' + productId;
    const handler = (msg: { payload?: unknown }) => {
      if (msg.payload && typeof msg.payload === 'object') {
        updateTelemetry(msg.payload as TelemetrySnapshot);
      }
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [productId, product?.iotThingName, subscribe, unsubscribe, updateTelemetry]);

  // ── WebSocket entity subscription (simulation state + shadow heartbeat) ───
  React.useEffect(() => {
    const topic = 'entity:products:' + productId;
    const handler = (msg: { payload?: unknown }) => {
      if (!msg.payload || typeof msg.payload !== 'object') return;
      const payload = msg.payload as Partial<Product> & { shadowTsMs?: number; type?: string };
      const shadowTsMs = payload.shadowTsMs;
      const shadowIso = typeof shadowTsMs === 'number' ? new Date(shadowTsMs).toISOString() : null;
      const { shadowTsMs: _shadowTsMs, type: _eventType, ...productUpdate } = payload;

      setProduct((prev) => {
        if (!prev) return prev;
        const next: Product = { ...prev, ...productUpdate };
        if (shadowIso) {
          next.lastShadowAt = maxIsoTimestamp(shadowIso, prev.lastShadowAt) ?? shadowIso;
          next.lastSignalAt = maxIsoTimestamp(shadowIso, prev.lastSignalAt) ?? shadowIso;
        }
        return next;
      });

      if (shadowIso) {
        setTelemetry((prev) => {
          if (!prev) {
            return {
              productId,
              time: null,
              measures: {},
              components: [],
              lastShadowAt: shadowIso,
              lastSignalAt: shadowIso,
            };
          }
          return {
            ...prev,
            lastShadowAt: maxIsoTimestamp(shadowIso, prev.lastShadowAt),
            lastSignalAt: maxIsoTimestamp(shadowIso, prev.lastSignalAt),
          };
        });
      }
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [productId, subscribe, unsubscribe]);

  // Live product-access changes for the signed-in user (grant removed/updated/approved).
  React.useEffect(() => {
    if (!currentUserId) return;
    const topic = `entity:product-access:user:${currentUserId}`;
    const handler = (msg: { payload?: unknown }) => {
      const payload = msg.payload as { productId?: string } | undefined;
      if (payload?.productId && payload.productId !== productId) return;
      void refreshAccess();
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [currentUserId, productId, subscribe, unsubscribe, refreshAccess]);

  const healthTelemetryAt = maxIsoTimestamp(
    telemetry?.lastTelemetryAt ?? telemetry?.time,
    product?.lastTelemetryAt,
  );
  const healthShadowAt = maxIsoTimestamp(telemetry?.lastShadowAt, product?.lastShadowAt);
  const healthSignalAt = resolveLastSignalAt(
    maxIsoTimestamp(telemetry?.lastSignalAt, product?.lastSignalAt),
    healthTelemetryAt,
    healthShadowAt,
  );

  // ── Datapoint ListView ────────────────────────────────────────────────────

  // Derive measures for the active source (product-level or component)
  const activeMeasures = React.useMemo<Record<string, unknown>>(() => {
    if (!telemetry) return {};
    const activeSource = telemetrySource
      ? telemetry.components?.find((cs) => cs.componentId === telemetrySource)
      : null;
    return (activeSource ? activeSource.measures : (telemetry.measures ?? {})) as Record<string, unknown>;
  }, [telemetry, telemetrySource]);

  // Stable row identifiers — labels from catalog, values read live from activeMeasures in renderCard.
  // Only rebuilds when the SET of keys changes, not when values change, so ListView
  // does not reset its page on every WS telemetry event.
  const datapointRowKeys = React.useMemo<DatapointRow[]>(() => {
    const catalogKeySet = new Set(catalogKeys.map((ck) => ck.key));
    const catalogRows = catalogKeys
      .filter((ck) => activeMeasures[ck.key] !== undefined)
      .map((ck) => ({ id: ck.key, key: ck.key, label: ck.label }));
    const extraRows = Object.keys(activeMeasures)
      .filter((k) => !catalogKeySet.has(k))
      .sort()
      .map((k) => ({ id: k, key: k, label: (activeMeasures[k] as any)?.label ?? k }));
    return [...catalogRows, ...extraRows];
  }, [catalogKeys, activeMeasures]);

  const datapointRowKeysRef = React.useRef(datapointRowKeys);
  datapointRowKeysRef.current = datapointRowKeys;

  // contextKey changes only when the key SET changes, not on value updates
  const datapointContextKey = datapointRowKeys.map((r) => r.key).join(',');

  // Stable fetchList — reads from ref so ListView never resets page on value-only WS updates.
  // WS subscription note: the telemetry channel delivers all measures in one payload per product.
  // Per-key server-side filtering would require per-measure pub/sub channels — deferred.
  const fetchDatapointList = React.useCallback(
    (params: import("../../../ui/api/listApi.types").ListParams): Promise<import("../../../ui/api/listApi.types").ListResult<DatapointRow>> => {
      let rows = datapointRowKeysRef.current;
      if (params.search?.trim()) {
        const q = params.search.toLowerCase();
        rows = rows.filter((r) => r.label.toLowerCase().includes(q) || r.key.toLowerCase().includes(q));
      }
      const start = ((params.page ?? 1) - 1) * (params.limit ?? 10);
      return Promise.resolve({ data: rows.slice(start, start + (params.limit ?? 10)), total: rows.length });
    },
    [],
  );

  // ── Components ────────────────────────────────────────────────────────────
  const [components, setComponents] = React.useState<Component[]>([]);
  const [componentModalOpen, setComponentModalOpen] = React.useState(false);
  const [editingComponent, setEditingComponent] = React.useState<Component | undefined>(undefined);
  const [viewingComponent, setViewingComponent] = React.useState<Component | null>(null);
  const { confirm: confirmComponent, ConfirmModal: ComponentConfirmModal } = useConfirmModal();

  // ── Subscriptions ─────────────────────────────────────────────────────────
  const [subscriptionsModalOpen, setSubscriptionsModalOpen] = React.useState(false);
  const [subscriptionsTargetUserId, setSubscriptionsTargetUserId] = React.useState<string | undefined>(undefined);
  const [subscriptionsTargetLabel, setSubscriptionsTargetLabel] = React.useState<string | undefined>(undefined);
  const [subscriptionsLockUser, setSubscriptionsLockUser] = React.useState(false);
  const [subscriptionsSkipUserPrefill, setSubscriptionsSkipUserPrefill] = React.useState(false);
  const [subscribersRefreshKey, setSubscribersRefreshKey] = React.useState(0);
  const isSuperAdmin = Boolean(userPayload?.roles?.includes("super-admin"));

  React.useEffect(() => {
    if (!token) return;
    productsApi.fetchComponents(token, productId).then(setComponents).catch(() => setComponents([]));
  }, [token, productId]);

  const fetchComponentsList = React.useCallback((params: ListParams): Promise<ListResult<Component>> => {
    const start = (params.page - 1) * params.limit;
    const filtered = params.search
      ? components.filter((c) =>
          [c.name, c.componentType, c.serial, c.model, c.iotComponentId]
            .some((v) => v?.toLowerCase().includes(params.search!.toLowerCase()))
        )
      : components;
    return Promise.resolve({ data: filtered.slice(start, start + params.limit), total: filtered.length });
  }, [components]);

  const componentColumns = React.useMemo<ColumnDefinition<Component>[]>(() => [
    { id: 'componentType', label: 'Type', render: (row) => <span className={s.componentTypeChip}>{row.componentType}</span> },
    { id: 'name', label: 'Name', render: (row) => <span style={{ fontSize: 13 }}>{row.name ?? '—'}</span> },
    { id: 'serial', label: 'Serial', render: (row) => <span style={{ fontSize: 12, fontFamily: 'monospace' }}>{row.serial ?? '—'}</span> },
    { id: 'model', label: 'Model', render: (row) => <span style={{ fontSize: 13 }}>{row.model ?? '—'}</span> },
    { id: 'fwVersion', label: 'Firmware', render: (row) => <span style={{ fontSize: 12 }}>{row.fwVersion ?? '—'}</span> },
  ], []);


  const canEditProduct =
    myAccess?.tier === "edit" || myAccess?.tier === "full";

  const getComponentContextActions = React.useCallback((row: Component): import("../../../ui/components/ListView").ListViewContextAction[] => {
    if (!canEditProduct) return [];
    return [
      {
        id: "edit",
        label: "Edit",
        icon: <Pencil size={14} />,
        onClick: () => { setEditingComponent(row); setComponentModalOpen(true); },
      },
      {
        id: "remove",
        label: "Remove",
        icon: <Trash2 size={14} />,
        danger: true,
        onClick: async () => {
          if (!token) return;
          const confirmed = await confirmComponent(
            "Remove component",
            `Remove ${row.name ?? row.componentType} from this product? This cannot be undone.`,
            "Remove",
            "danger",
          );
          if (!confirmed) return;
          try {
            await productsApi.deleteComponent(token, productId, row.id);
            setComponents((prev) => prev.filter((c) => c.id !== row.id));
          } catch { /* ignore */ }
        },
      },
    ];
  }, [token, productId, confirmComponent, canEditProduct]);

  // ── Historical data (all products) ───────────────────────────────────────
  const defaultHistoryKeys = React.useMemo(
    () => initialHistoryKeysForProductLine(product?.productModel?.productLine),
    [product?.productModel?.productLine],
  );

  /** When Timestream keys don't match the initial defaults, remount with corrected filter. */
  const [historyKeyFilterOverride, setHistoryKeyFilterOverride] = React.useState<string[] | null>(null);
  /** Set by an Insight's "View data" to open Historical Data on the evidence window. */
  const [historyWindowOverride, setHistoryWindowOverride] = React.useState<{ start: string; end: string } | null>(null);

  React.useEffect(() => {
    setHistoryKeyFilterOverride(null);
    setHistoryWindowOverride(null);
  }, [productId]);

  const effectiveHistoryKeys = historyKeyFilterOverride ?? defaultHistoryKeys;

  const [historyStatus, setHistoryStatus] = React.useState<HistoricalDataStatus>('loading');
  const [historySource, setHistorySource] = React.useState<'timestream' | 'partner-api' | null>(null);
  const [historyKnownParams, setHistoryKnownParams] = React.useState<Array<{ key: string; label: string }>>([]);
  /** Bumped when a simulation session starts so historical charts refetch seeded data. */
  const [historyRefreshKey, setHistoryRefreshKey] = React.useState(0);

  const historyDefaultFilters = React.useMemo(() => {
    if (historyWindowOverride) {
      return [
        { id: '__timeRange', value: [historyWindowOverride.start, historyWindowOverride.end] },
        { id: 'key', value: [...effectiveHistoryKeys] },
      ];
    }
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return [
      { id: '__timeRange', value: [start.toISOString(), end.toISOString()] },
      { id: 'key', value: [...effectiveHistoryKeys] },
    ];
  }, [effectiveHistoryKeys, historyRefreshKey, historyWindowOverride]);

  React.useEffect(() => {
    const [startIso, endIso] = historyDefaultFilters[0].value as [string, string];
    const startDate = new Date(startIso);
    const endDate = new Date(endIso);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return;
    const fmt = (d: Date) =>
      d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    setHistoryRangeLabel(`${fmt(startDate)} – ${fmt(endDate)}`);
  }, [historyDefaultFilters]);
  /** Flattened live WS points merged into the Chart view while the range is live-trailing. */
  const [chartLiveRows, setChartLiveRows] = React.useState<Array<{
    id: string;
    time: string;
    key: string;
    label: string;
    raw: string | number | null;
    unit: string;
  }>>([]);

  React.useEffect(() => {
    setChartLiveRows([]);
  }, [productId]);

  // Append each WS/latest telemetry snapshot into the live chart buffer
  React.useEffect(() => {
    if (!telemetry?.time || !telemetry.measures) return;
    const time = telemetry.time;
    const next: typeof chartLiveRows = [];
    for (const [k, m] of Object.entries(telemetry.measures as Record<string, unknown>)) {
      const scalar = unwrapTelemetryScalar(m as Record<string, unknown>);
      const display = scalar.value ?? scalar.raw;
      if (display !== null && typeof display === 'object') continue;
      next.push({
        id: `${time}:${k}`,
        time,
        key: k,
        label: scalar.label || k,
        raw: display as string | number | null,
        unit: scalar.unit || '',
      });
    }
    if (next.length === 0) return;
    setChartLiveRows((prev) => {
      const map = new Map(prev.map((r) => [r.id, r]));
      for (const r of next) map.set(r.id, r);
      const cutoff = Date.now() - 2 * 60 * 60 * 1000;
      return [...map.values()].filter((r) => {
        const t = parseTelemetryTimeMs(r.time);
        return t != null && t >= cutoff;
      });
    });
  }, [telemetry]);

  // Flattened: one row per (snapshot × measure key)
  interface HistoryRow {
    id: string;
    time: string;
    label: string;
    key: string;
    raw: string | number | null;
    formatted: string;
    unit: string;
  }


  const historyColumns = React.useMemo<ColumnDefinition<HistoryRow>[]>(() => [
    {
      id: 'time',
      label: 'Timestamp',
      sortable: false,
      width: 160,
      render: (row) => (
        <span style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {(parseTelemetryDate(row.time) ?? new Date(row.time)).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        </span>
      ),
    },
    { id: 'label', label: 'Label', sortable: false, render: (row) => <span>{row.label}</span> },
    {
      id: 'key',
      label: 'Parameters',
      sortable: false,
      filterType: 'checkbox',
      filterOptions: historyKnownParams.map(({ key, label }) => ({
        value: key,
        label,
        title: key !== label ? key : undefined,
      })),
      render: (row) => <span style={{ fontFamily: "monospace", fontSize: 11, opacity: 0.7 }}>{row.key}</span>,
    },
    {
      id: 'raw',
      label: 'Raw Value',
      sortable: false,
      render: (row) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {row.raw === null || row.raw === undefined ? <span style={{ opacity: 0.4 }}>—</span> : String(row.raw)}
        </span>
      ),
    },
    { id: 'formatted', label: 'Formatted Value', sortable: false, render: (row) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{row.formatted}</span> },
    { id: 'unit', label: 'Unit', sortable: false, render: (row) => <span style={{ opacity: row.unit ? 1 : 0.3 }}>{row.unit || '—'}</span> },
  ], [historyKnownParams]);

  const fetchHistoryList = React.useCallback(async (params: ListParams): Promise<ListResult<HistoryRow>> => {
    if (!token) return { data: [], total: 0 };
    const start = (params.filters?.timeRangeStart as string | undefined) ?? historyDefaultFilters[0].value[0];
    const end   = (params.filters?.timeRangeEnd   as string | undefined) ?? historyDefaultFilters[0].value[1];
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
        const fmt = (d: Date) =>
          d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
        setHistoryRangeLabel(`${fmt(startDate)} – ${fmt(endDate)}`);
      }
    } catch {
      /* ignore */
    }
    let keyFilter = Array.isArray(params.filters?.key) && (params.filters.key as string[]).length > 0
      ? params.filters.key as string[]
      : null;
    setHistoryStatus('loading');
    try {
      const page = await productsApi.fetchTelemetryRange(token, productId, { start, end, limit: 200 });
      setHistorySource(page._source ?? null);
      const seenParams = new Map<string, string>();
      const allRows: HistoryRow[] = [];
      for (const snapshot of page.results) {
        const time = snapshot.time ?? '';
        const measures = (snapshot.measures ?? {}) as Record<string, any>;
        for (const [k, m] of Object.entries(measures)) {
          // Telemetry enrichment sometimes nests another measure object under raw/value.
          const scalar = unwrapTelemetryScalar(m);
          const label = scalar.label || m?.label || k;
          if (!seenParams.has(k)) seenParams.set(k, label);
          const raw = scalar.raw;
          const val = scalar.value;
          const unit = scalar.unit || m?.unit || '';
          const display = val ?? raw;
          const formatted = display === null || display === undefined
            ? '—'
            : unit
              ? `${typeof display === 'number' ? display.toFixed(2) : display} ${unit}`
              : typeof display === 'number' ? display.toFixed(2) : String(display);
          allRows.push({ id: `${time}:${k}`, time, label, key: k, raw: display ?? raw, formatted, unit });
        }
      }
      if (seenParams.size > 0) {
        setHistoryKnownParams(prev => {
          const merged = new Map(prev.map(p => [p.key, p.label]));
          for (const [key, label] of seenParams) {
            if (!merged.has(key) || (label && label !== key)) merged.set(key, label);
          }
          const next = [...merged.entries()]
            .map(([key, label]) => ({ key, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
          const same =
            next.length === prev.length &&
            next.every((p, i) => p.key === prev[i]?.key && p.label === prev[i]?.label);
          return same ? prev : next;
        });
      }

      // If the active key filter matches none of the Timestream keys (e.g. sanctuary
      // defaults on a powersave cabinet), remap to product-appropriate defaults.
      if (keyFilter && allRows.length > 0) {
        const available = new Set(seenParams.keys());
        const hits = keyFilter.filter((k) => available.has(k));
        if (hits.length === 0) {
          const remapped = pickDefaultHistoryKeys(available);
          if (remapped.length > 0) {
            keyFilter = remapped;
            setHistoryKeyFilterOverride((prev) => {
              if (prev && prev.length === remapped.length && prev.every((k, i) => k === remapped[i])) {
                return prev;
              }
              return remapped;
            });
          } else {
            keyFilter = null;
          }
        }
      }

      const rows = keyFilter
        ? allRows.filter((r) => keyFilter!.includes(r.key))
        : allRows;

      setHistoryStatus(
        page.results.length === 0
          ? 'empty'
          : (product?.isSimulated ? 'simulated' : 'connected'),
      );
      const offset = (params.page - 1) * params.limit;
      return { data: rows.slice(offset, offset + params.limit), total: rows.length };
    } catch {
      setHistoryStatus('error');
      return { data: [], total: 0 };
    }
  }, [token, productId, product?.isSimulated, historyDefaultFilters]);

  const handleSimulationChange = React.useCallback((updates: Partial<Product>) => {
    setProduct((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  // When simulation starts/stops (manual or auto-expiry via WS), refresh or clear historical chart.
  const prevSimulatingRef = React.useRef<boolean | undefined>(undefined);
  React.useEffect(() => {
    if (!product?.isSimulated) {
      prevSimulatingRef.current = product?.isSimulating;
      return;
    }
    const was = prevSimulatingRef.current;
    const now = product.isSimulating;
    prevSimulatingRef.current = now;

    if (was === undefined) {
      // Initial load: paused sims should not show leftover/empty-fetch status as "connected"
      if (!now) setHistoryStatus('simulationPaused');
      return;
    }
    if (was === now) return;

    if (now) {
      // Seed is ready when start returns; remount ListView with a fresh 24h window.
      setHistoryRefreshKey((k) => k + 1);
      setChartLiveRows([]);
    } else {
      setChartLiveRows([]);
      setHistoryStatus('simulationPaused');
    }
  }, [product?.isSimulated, product?.isSimulating]);

  const handleBack = () => navigate({ to: "/workspace/products" });
  const handleEdit = () =>
    navigate({ to: "/workspace/products/$productId/edit", params: { productId } });

  // Simulation helpers (extracted from SimulationControlCard for inline use)
  const [simLoading, setSimLoading] = React.useState<string | null>(null);
  const [simError, setSimError] = React.useState<string | null>(null);

  const handleSimStart = React.useCallback(async (durationMs: number) => {
    if (!token) return;
    setSimLoading("start");
    setSimError(null);
    try {
      const result = await productsApi.startProductSimulation(token, productId, durationMs);
      handleSimulationChange({ isSimulating: result.isSimulating, simulationExpAt: result.simulationExpAt, simulationStartedAt: result.simulationStartedAt });
    } catch (err: any) {
      setSimError(err?.message || "Failed to start simulation.");
    } finally {
      setSimLoading(null);
    }
  }, [token, productId, handleSimulationChange]);

  const handleSimPause = React.useCallback(async () => {
    if (!token) return;
    const ok = await confirmComponent(
      "Pause simulation?",
      "The simulation will be paused and its state saved. You can resume it at any time.",
      "Pause",
    );
    if (!ok) return;
    setSimLoading("pause");
    setSimError(null);
    try {
      const result = await productsApi.pauseProductSimulation(token, productId);
      handleSimulationChange({ isSimulating: result.isSimulating, simulationExpAt: result.simulationExpAt, simulationStartedAt: null });
    } catch (err: any) {
      setSimError(err?.message || "Failed to pause simulation.");
    } finally {
      setSimLoading(null);
    }
  }, [token, productId, handleSimulationChange, confirmComponent]);

  if (loading) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <Text level="body-medium" style={{ color: "#9CA3AF" }}>{tx("shared.common.loading")}</Text>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <button className={s.backLink} onClick={handleBack}>
            <ArrowLeft size={14} /> Products
          </button>
          <div className={s.requestAccessWrap}>
            <Card maxWidth={520}>
              <div className={s.cardInner}>
                <RequestProductAccessForm
                  mode="request"
                  productName={deniedProductName}
                  initialTier={accessPendingTier ?? "view"}
                  pending={accessPending}
                  pendingTier={accessPendingTier}
                  submitting={requestingAccess}
                  cancelling={cancellingAccessRequest}
                  error={requestAccessError}
                  onSubmit={submitAccessRequest}
                  onCancelRequest={cancelAccessRequest}
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <button className={s.backLink} onClick={handleBack}>
            <ArrowLeft size={14} /> Products
          </button>
          <Callout variant="error">{error ?? "Product not found."}</Callout>
        </div>
      </div>
    );
  }

  const hasLocation = product.address || product.city || product.state || product.country;
  const myStructuredTier = structuredAccessTier(myAccess?.tier);
  const changeRequestInitialTier =
    accessPendingTier ?? preferredRequestTier(myStructuredTier);

  // Simulation outline: green when active, blue when paused
  const simulationHighlight = product.isSimulated
    ? product.isSimulating
      ? ("success" as const)
      : ("info" as const)
    : false;

  // Header badges
  const headerBadges: React.ReactNode[] = [];
  const owningOrg = myAccess?.owningOrganization;
  if (owningOrg?.name) {
    headerBadges.push(
      <OrgThumbnail
        key="owning-org"
        name={owningOrg.name}
        logoUrl={owningOrg.logoUrl}
        size={22}
        onClick={() =>
          navigate({ to: "/workspace/organizations/$orgId", params: { orgId: owningOrg.id } })
        }
      />,
    );
  }
  if (myAccess?.tier) {
    headerBadges.push(
      <ProductAccessTierBadge
        key="access"
        tier={myAccess.tier}
        title={formatProductAccessTooltip(myAccess)}
        showShield
        onClick={() => {
          setRequestAccessError(null);
          setRequestModalOpen(true);
        }}
      />
    );
  }
  if (product.status === 'inactive' || product.status === 'decommissioned') {
    headerBadges.push(<StatusBadge key="status" status={product.status} />);
  }
  if (product.smartLink?.linkEnabled) {
    headerBadges.push(
      <SmartLinkSyncStatusBadge
        key="smart-link"
        status={product.smartLink.syncStatus}
        label={t("pro.system.smartLink.badge")}
      />,
    );
  }
  if (product.isSimulated) {
    headerBadges.push(
      <span
        key="sim"
        className={s.statusBadge}
        title={t("pro.products.simulatedTelemetryBanner")}
        style={{ background: "#EFF6FF", color: "#1D4ED8", borderColor: "#BFDBFE" }}
      >
        {tx("pro.products.simBadge")}
      </span>
    );
  }
  if (activeAlertSummaries.length > 0) {
    headerBadges.push(
      <ActiveAlertsCountBadge key="active-alerts" alerts={activeAlertSummaries} />,
    );
  }

  // Context actions for page-level operations
  // HeaderActionsRow reverses the array, so push right→left (Edit rightmost).
  const contextActions: ContextAction[] = [];
  if (canEditProduct) {
    contextActions.push({
      id: "edit",
      label: "Edit",
      icon: <Pencil size={16} />,
      color: "primary",
      onClick: handleEdit,
    });
    contextActions.push({
      id: "settings",
      label: "Settings",
      icon: <Settings size={16} />,
      color: "neutral",
      onClick: () => navigate({ to: "/workspace/products/$productId/settings", params: { productId } }),
    });
  }
  contextActions.push({
    id: "subscribe",
    label: t("pro.products.subscriptions.subscribe"),
    icon: <Bell size={16} />,
    color: "neutral",
    onClick: () => {
      setSubscriptionsSkipUserPrefill(false);
      setSubscriptionsTargetUserId(currentUserId ?? undefined);
      setSubscriptionsTargetLabel(undefined);
      setSubscriptionsLockUser(!isSuperAdmin);
      setSubscriptionsModalOpen(true);
    },
  });

  // Super-admins always see Insights (pro-api applies the same rule), so Lion staff can check a product before its org is enabled.
  const insightsEnabled = insightsFlagEnabled || isSuperAdmin;

  const handleViewInsightEvidence = (window: InsightEvidenceWindow) => {
    setHistoryWindowOverride({ start: window.start, end: window.end });
    if (window.keys?.length) setHistoryKeyFilterOverride(window.keys);
    setHistoryRefreshKey((k) => k + 1);
    const id = SECTION_IDS.historical(productId);
    if (!isExpanded(id)) toggle(id);
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const bluetoothOnly = productsApi.isBluetoothOnlyLine(product.productModel?.productLine);

  // Section navigation
  const sectionLayout = bindSectionLayout([
    ...(product.isSimulated ? [{ id: SECTION_IDS.simulation(productId), label: t("pro.products.simulation"), icon: <Activity size={14} /> }] : []),
    ...(insightsEnabled ? [{ id: SECTION_IDS.insights(productId), label: t("pro.products.insights.title"), icon: <Sparkles size={14} /> }] : []),
    { id: SECTION_IDS.system(productId), label: t("pro.products.system"), icon: <Box size={14} /> },
    { id: SECTION_IDS.images(productId), label: t("pro.products.images"), icon: <ImageIcon size={14} /> },
    ...(hasLocation ? [{ id: SECTION_IDS.location(productId), label: t("pro.products.location"), icon: <MapPin size={14} /> }] : []),
    { id: SECTION_IDS.components(productId), label: t("pro.products.components"), icon: <Cpu size={14} /> },
    ...(canManageAccess ? [{ id: SECTION_IDS.access(productId), label: t("pro.products.productAccess"), icon: <Shield size={14} /> }] : []),
    { id: SECTION_IDS.subscribers(productId), label: t("pro.products.subscribers"), icon: <Users size={14} /> },
    { id: SECTION_IDS.realtime(productId), label: t("pro.products.realTimeData"), icon: <Database size={14} /> },
    { id: SECTION_IDS.historical(productId), label: t("pro.products.historicalData"), icon: <BarChart3 size={14} /> },
    { id: SECTION_IDS.alerts(productId), label: "Alerts", icon: <AlertTriangle size={14} /> },
  ]);

  return (
    <>
      <DetailPageLayout
        backLink={{
          label: "Products",
          onClick: handleBack,
        }}
        header={{
          media: (
            <div className={s.headerThumb}>
              {headerImageUrl ? (
                <img src={headerImageUrl} alt="" className={s.headerThumbImg} />
              ) : (
                <Box size={28} color="#9CA3AF" aria-hidden />
              )}
            </div>
          ),
          title: product.name,
          badges: headerBadges,
        }}
        contextActions={contextActions}
        {...sectionLayout}
      >

        {/* Simulation control — CollapsibleSection for simulated products */}
        {product.isSimulated && (
          <CollapsibleSection
            id={SECTION_IDS.simulation(productId)}
            title={t("pro.products.simulation")}
            icon={<Activity size={20} />}
            expanded={isExpanded(SECTION_IDS.simulation(productId))}
            onToggleExpanded={() => toggle(SECTION_IDS.simulation(productId))}
            highlight={simulationHighlight}
            statusIndicators={
              canEditProduct
                ? product.isSimulating
                  ? [
                      <Button
                        key="pause"
                        type="button"
                        color="primary"
                        variant="outline"
                        iconLeft={<Pause size={14} />}
                        onClick={handleSimPause}
                        loading={simLoading === "pause"}
                        disabled={simLoading !== null}
                        style={{ height: 34 }}
                      >
                        Pause
                      </Button>,
                    ]
                  : [
                      <StartSimulationControl
                        key="start"
                        token={token}
                        productId={productId}
                        onStarted={handleSimulationChange}
                        selectedDuration={simDuration}
                        onDurationChange={setSimDuration}
                      />,
                    ]
                : undefined
            }
          >
            <div className={s.cardInner}>
              {simError && <Callout variant="error">{simError}</Callout>}
              {product.isSimulating ? (
                <SimulationAlertControls
                  token={token}
                  productId={productId}
                  alerts={catalogAlerts}
                  canEdit={canEditProduct}
                  renderStatus={(alertProps) => (
                    <SimulationStatusInner product={product} {...alertProps} />
                  )}
                />
              ) : (
                <SimulationStatusInner product={product} />
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Insights */}
        {insightsEnabled && (
          <CollapsibleSection
            id={SECTION_IDS.insights(productId)}
            title={t("pro.products.insights.title")}
            subtitle={
              insightsOpenCount != null
                ? t("pro.products.insights.subtitle", { count: insightsOpenCount })
                : undefined
            }
            icon={<Sparkles size={20} />}
            expanded={isExpanded(SECTION_IDS.insights(productId))}
            onToggleExpanded={() => toggle(SECTION_IDS.insights(productId))}
          >
            {bluetoothOnly ? (
              <div className={s.cardInner}><ComingSoonCard title={t("pro.products.bluetoothOnly.title")} message={t("pro.products.bluetoothOnly.message")} /></div>
            ) : (
            <>
            <div className={s.cardInner}>
              <ProductInsightsSection
                productId={productId}
                token={token}
                showUsage={isSuperAdmin}
                canDismiss={canEditProduct}
                refreshKey={insightsRefreshKey}
                onOpenCountChange={setInsightsOpenCount}
                onViewEvidence={handleViewInsightEvidence}
              />
            </div>
            </>
            )}
          </CollapsibleSection>
        )}

        {/* System — merged Overview + Identifiers + System */}
        <CollapsibleSection
          id={SECTION_IDS.system(productId)}
          title={t("pro.products.system")}
          icon={<Box size={20} />}
          expanded={isExpanded(SECTION_IDS.system(productId))}
          onToggleExpanded={() => toggle(SECTION_IDS.system(productId))}
          statusIndicators={[
            <DeviceHealthIndicator
              key="health"
              lastSignalAt={healthSignalAt}
              lastTelemetryAt={healthTelemetryAt}
              lastShadowAt={healthShadowAt}
              productCreatedAt={product?.createdAt}
              serviceStartedAt={serviceStartedAt}
              isSimulated={product.isSimulated}
              isSimulating={product.isSimulating}
              simulationStartedAt={product.simulationStartedAt}
              simulationExpAt={product.simulationExpAt}
              size="md"
              showLabel
            />,
          ]}
        >
          <div className={s.cardInner}>
              <div className={s.fieldRow}>
                <Field label={t("pro.products.model")} value={product.productModel?.name} />
                <Field label={t("pro.products.productLine")} value={
                  product.productModel?.productLine
                    ? product.productModel.productLine.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                    : null
                } />
              </div>
              <div className={s.fieldRow}>
                <Field label="Organization" value={myAccess?.owningOrganization?.name ?? null} />
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.products.source")} value={product.source === 'lion_energy' ? 'Lion Energy' : 'Solarman'} />
                <Field label={t("pro.products.simulated")} value={product.isSimulated ? "Yes" : "No"} />
              </div>
              {product.pvCapacityW && (
                <div className={s.fieldRow}>
                  <Field label={t("pro.products.solarCapacity")} value={`${product.pvCapacityW} W`} />
                </div>
              )}
              <div className={s.fieldRow}>
                <Field label={t("pro.products.assetTag")} value={product.assetTag} />
                <Field label={t("pro.products.serialNumber")} value={product.serial} />
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.products.macAddress")} value={product.macAddress} />
                <Field label={t("pro.products.modelNumber")} value={product.productModel?.modelNumber} />
              </div>
              <div className={s.fieldRow}>
                <Field label={t("pro.products.iotThingName")} value={product.iotThingName ?? "Not configured"} />
              </div>
              <div className={s.fieldRow}>
                <Field label="ID" value={product.id} />
              </div>
            <div className={s.fieldRow}>
              <Field label={t("pro.products.created")} value={formatDate(product.createdAt)} />
              <Field label={t("pro.products.updated")} value={formatDate(product.updatedAt)} />
            </div>
          </div>
        </CollapsibleSection>

        {/* Images */}
        {token && (
          <CollapsibleSection
            id={SECTION_IDS.images(productId)}
            title={t("pro.products.images")}
            subtitle={
              imageCount != null
                ? `${imageCount} image${imageCount !== 1 ? "s" : ""}`
                : undefined
            }
            icon={<ImageIcon size={20} />}
            expanded={isExpanded(SECTION_IDS.images(productId))}
            onToggleExpanded={() => toggle(SECTION_IDS.images(productId))}
          >
            <div className={s.cardInner}>
              <ProductImageGallery
                productId={product.id}
                accessToken={token}
                readOnly={!canEditProduct}
                onImageCountChange={setImageCount}
              />
            </div>
          </CollapsibleSection>
        )}

        {/* Location */}
        {hasLocation && (
          <CollapsibleSection
            id={SECTION_IDS.location(productId)}
            title={t("pro.products.location")}
            icon={<MapPin size={20} />}
            expanded={isExpanded(SECTION_IDS.location(productId))}
            onToggleExpanded={() => toggle(SECTION_IDS.location(productId))}
          >
            <div className={s.cardInner}>
                <div className={s.fieldRow}>
                  <Field label={t("pro.products.address")} value={product.address} />
                  <Field label={t("pro.products.address2")} value={product.addressSecondary} />
                </div>
                <div className={s.fieldRow}>
                  <Field label={t("pro.products.city")} value={product.city} />
                  <Field label={t("pro.products.state")} value={product.state} />
                </div>
                <div className={s.fieldRow}>
                  <Field label={t("pro.products.zip")} value={product.zip} />
                  <Field label={t("pro.products.country")} value={product.country} />
                </div>
                <div className={s.fieldRow}>
                  <Field label={t("pro.products.timezone")} value={product.timezone} />
                </div>
              {(product.latitude || product.longitude) && (
                <div className={s.fieldRow}>
                  <Field label={t("pro.products.latitude")} value={product.latitude ?? undefined} />
                  <Field label={t("pro.products.longitude")} value={product.longitude ?? undefined} />
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Components */}
        <CollapsibleSection
          id={SECTION_IDS.components(productId)}
          title={t("pro.products.components")}
          subtitle={`${components.length} component${components.length !== 1 ? 's' : ''}`}
          icon={<Cpu size={20} />}
          expanded={isExpanded(SECTION_IDS.components(productId))}
          onToggleExpanded={() => toggle(SECTION_IDS.components(productId))}
        >
          {components.length === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "36px 0" }}>
              <Text level="body-small" style={{ color: "#9CA3AF" }}>
                No components have been added to this product yet.
              </Text>
              {canEditProduct ? (
                <Button
                  type="button"
                  color="primary"
                  variant="outline"
                  iconLeft={<Plus size={14} />}
                  onClick={() => { setEditingComponent(undefined); setComponentModalOpen(true); }}
                >
                  Add component
                </Button>
              ) : null}
            </div>
          ) : (
            <ListView<Component>
              key={components.length}
              listId={`product-components-${productId}`}
              columns={componentColumns}
              fetchList={fetchComponentsList}
              enabledViews={['stack', 'deck']}
              headerActions={
                canEditProduct
                  ? [
                      {
                        id: "add",
                        label: "Add component",
                        icon: <Plus size={14} />,
                        onClick: () => { setEditingComponent(undefined); setComponentModalOpen(true); },
                      },
                    ]
                  : undefined
              }
              renderStackCard={(row, ctx) => (
                <ListViewComponentCard
                  layout="stack"
                  component={row}
                  ctx={ctx}
                />
              )}
              renderCard={(row, ctx) => (
                <ListViewComponentCard
                  layout="deck"
                  component={row}
                  ctx={ctx}
                />
              )}
              getContextActions={getComponentContextActions}
              onRowClick={(row) => {
                if (canEditProduct) {
                  setEditingComponent(row);
                  setComponentModalOpen(true);
                } else {
                  setViewingComponent(row);
                }
              }}
              enableSelection={canEditProduct}
              compact
              disableLocalPersistence
              defaultPageSize={10}
            />
          )}
        </CollapsibleSection>

        {/* Product Access */}
        {canManageAccess && (
          <CollapsibleSection
            id={SECTION_IDS.access(productId)}
            title={t("pro.products.productAccess")}
            subtitle={
              accessCount != null
                ? `${accessCount} user${accessCount !== 1 ? "s" : ""} with access`
                : undefined
            }
            icon={<Shield size={20} />}
            expanded={isExpanded(SECTION_IDS.access(productId))}
            onToggleExpanded={() => toggle(SECTION_IDS.access(productId))}
          >
            <ProductAccessSection
              productId={productId}
              productName={product.name}
              token={token}
              canManage={canManageAccess}
              hideChrome
              onAccessCountChange={setAccessCount}
            />
          </CollapsibleSection>
        )}

        {/* Subscribers */}
        <CollapsibleSection
          id={SECTION_IDS.subscribers(productId)}
          title={t("pro.products.subscribers")}
          subtitle={
            subscriberCount != null
              ? `${subscriberCount} subscriber${subscriberCount !== 1 ? "s" : ""}`
              : undefined
          }
          icon={<Users size={20} />}
          expanded={isExpanded(SECTION_IDS.subscribers(productId))}
          onToggleExpanded={() => toggle(SECTION_IDS.subscribers(productId))}
        >
          {bluetoothOnly ? (
            <div className={s.cardInner}><ComingSoonCard title={t("pro.products.bluetoothOnly.title")} message={t("pro.products.bluetoothOnly.message")} /></div>
          ) : (
          <>
          <ProductSubscribersSection
            productId={productId}
            token={token}
            hideChrome
            refreshKey={subscribersRefreshKey}
            onSubscriberCountChange={setSubscriberCount}
            onAddSubscription={() => {
              setSubscriptionsSkipUserPrefill(true);
              setSubscriptionsTargetUserId(undefined);
              setSubscriptionsTargetLabel(undefined);
              setSubscriptionsLockUser(false);
              setSubscriptionsModalOpen(true);
            }}
            onEdit={(userId, displayName) => {
              setSubscriptionsSkipUserPrefill(false);
              setSubscriptionsTargetUserId(userId);
              setSubscriptionsTargetLabel(displayName);
              setSubscriptionsLockUser(true);
              setSubscriptionsModalOpen(true);
            }}
          />
          </>
          )}
        </CollapsibleSection>

        {/* Real-time Data */}
        <CollapsibleSection
          id={SECTION_IDS.realtime(productId)}
          title={t("pro.products.realTimeData")}
          subtitle={`${datapointRowKeys.length} data point${datapointRowKeys.length !== 1 ? "s" : ""}`}
          icon={<Database size={20} />}
          expanded={isExpanded(SECTION_IDS.realtime(productId))}
          onToggleExpanded={() => toggle(SECTION_IDS.realtime(productId))}
          statusIndicators={bluetoothOnly ? undefined : [
            <RealtimeIndicator
              key="realtime"
              lastTelemetryAt={telemetry?.lastTelemetryAt ?? telemetry?.time ?? null}
              productCreatedAt={product.createdAt}
              serviceStartedAt={serviceStartedAt}
              domain={telemetry?.domain ?? product.domain ?? resolveProductDomain(product.productModel?.productLine)}
              isSimulated={product.isSimulated}
              isSimulating={product.isSimulating}
            />,
          ]}
        >
          {bluetoothOnly ? (
            <div className={s.cardInner}><ComingSoonCard title={t("pro.products.bluetoothOnly.title")} message={t("pro.products.bluetoothOnly.message")} /></div>
          ) : (
          <>
          <div className={s.cardInner}>

              {!product.iotThingName && !product.isSimulated && (
                <Text level="body-small" style={{ color: "#9CA3AF" }}>
                  IoT thing name not configured — set it in the Edit page to enable telemetry.
                </Text>
              )}

              {telemetryError && <Callout variant="error">{telemetryError}</Callout>}

              {telemetryOffline && !telemetryError && (() => {
                const productAgeMs = product?.createdAt ? Date.now() - new Date(product.createdAt).getTime() : Infinity;
                return productAgeMs < 10 * 60 * 1000 ? (
                  <Callout variant="info">
                    Waiting for first device report — data typically arrives within 5 minutes.
                  </Callout>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 0" }}>
                    <Text level="body-small" style={{ color: "#9CA3AF", textAlign: "center" }}>
                      {DEVICE_OFFLINE_MESSAGE}
                    </Text>
                  </div>
                );
              })()}

              {telemetryStale && !telemetryOffline && !telemetryError && (telemetryStaleAgeMs === null || telemetryStaleAgeMs > 10 * 60 * 1000) && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 6, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: "#B45309" }}>
                    {telemetry?.time
                      ? `Showing last known data · Last reading: ${new Date(telemetry.time).toLocaleString()}`
                      : "Showing last known data — live connection unavailable"
                    }
                  </span>
                </div>
              )}

              {product.isSimulated && !product.isSimulating && !telemetryLoading && !telemetryError && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "16px 0" }}>
                  <Text level="body-small" style={{ color: "#9CA3AF", textAlign: "center" }}>
                    Simulation is paused — start a session to view live telemetry data.
                  </Text>
                  {canEditProduct ? (
                    <StartSimulationControl
                      token={token}
                      productId={product.id}
                      onStarted={handleSimulationChange}
                      selectedDuration={simDuration}
                      onDurationChange={setSimDuration}
                    />
                  ) : null}
                </div>
              )}

              {telemetry && (!product.isSimulated || product.isSimulating) && !telemetryOffline && (
                <>
                  {/* Source selector (Product vs per-component) */}
                  {(telemetry.components?.length ?? 0) > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                      <button
                        type="button"
                        onClick={() => setTelemetrySource(null)}
                        style={{
                          padding: "4px 12px", borderRadius: 9999, fontSize: 12, fontWeight: 500,
                          border: "1px solid",
                          borderColor: telemetrySource === null ? "#3B82F6" : "#D1D5DB",
                          background: telemetrySource === null ? "#EFF6FF" : "transparent",
                          color: telemetrySource === null ? "#1D4ED8" : "#6B7280",
                          cursor: "pointer",
                        }}
                      >
                        Product
                      </button>
                      {telemetry.components.map((cs) => {
                        const label = cs.name
                          ? `${cs.name} (${cs.componentType})`
                          : cs.componentIndex != null
                          ? `${cs.componentType.toUpperCase()} #${cs.componentIndex}`
                          : cs.componentType.toUpperCase();
                        return (
                          <button
                            key={cs.componentId}
                            type="button"
                            onClick={() => setTelemetrySource(cs.componentId)}
                            style={{
                              padding: "4px 12px", borderRadius: 9999, fontSize: 12, fontWeight: 500,
                              border: "1px solid",
                              borderColor: telemetrySource === cs.componentId ? "#3B82F6" : "#D1D5DB",
                              background: telemetrySource === cs.componentId ? "#EFF6FF" : "transparent",
                              color: telemetrySource === cs.componentId ? "#1D4ED8" : "#6B7280",
                              cursor: "pointer",
                              fontFamily: cs.name ? undefined : "monospace",
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Datapoints ListView */}
                  {datapointRowKeys.length > 0 ? (
                    <ListView<DatapointRow>
                      listId={`product-telemetry-${productId}`}
                      columns={[]}
                      fetchList={fetchDatapointList}
                      contextKey={datapointContextKey}
                      enabledViews={['deck', 'stack']}
                      compact
                      defaultPageSize={10}
                      forcedFlashIds={datapointVersions}
                      disableLocalPersistence
                      searchPlaceholder={t("pro.products.searchDatapoints")}
                      emptyTitle={t("pro.products.noDatapoints")}
                      emptySubtitle={t("pro.products.noMatchingTelemetryKeys")}
                      renderCard={(row) => {
                        const measure = activeMeasures[row.key] as Record<string, unknown> | undefined;
                        if (!measure) return null;
                        const catalogKey = catalogKeys.find((ck) => ck.key === row.key);
                        const label = (measure as any).label ?? row.label;
                        const valueStr = formatMeasureValue(measure, catalogKey);
                        const unitStr = (measure as any).unit as string | undefined;
                        return (
                          <div className={s.datapointDeckCard}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}>
                              <span className={s.datapointSectionLabel} style={{ marginBottom: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{label}</span>
                              {product.isSimulated && (
                                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', padding: '1px 5px', borderRadius: 3, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', flexShrink: 0, textTransform: 'uppercase' }}>{tx("pro.products.simBadge")}</span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, lineHeight: 1.1 }}>
                              <span className={s.datapointValue}>{valueStr}</span>
                              {unitStr && <span className={s.datapointSectionLabel} style={{ marginBottom: 0, fontSize: 11, fontWeight: 500 }}>{unitStr}</span>}
                            </div>
                            <span className={s.datapointKey}>{row.key}</span>
                          </div>
                        );
                      }}
                      renderStackCard={(row) => {
                        const measure = activeMeasures[row.key] as Record<string, unknown> | undefined;
                        if (!measure) return null;
                        const catalogKey = catalogKeys.find((ck) => ck.key === row.key);
                        const label = (measure as any).label ?? row.label;
                        const valueStr = formatMeasureValue(measure, catalogKey);
                        const unitStr = (measure as any).unit as string | undefined;
                        return (
                          <div className={s.datapointStackCard}>
                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <span className={s.datapointSectionLabel} style={{ marginBottom: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                              <span className={s.datapointKey}>{row.key}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, flexShrink: 0 }}>
                              <span className={s.datapointValue}>{valueStr}</span>
                              {unitStr && <span className={s.datapointSectionLabel} style={{ marginBottom: 0, fontSize: 12, fontWeight: 400 }}>{unitStr}</span>}
                            </div>
                            {product.isSimulated && (
                              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', padding: '1px 5px', borderRadius: 3, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', flexShrink: 0, textTransform: 'uppercase' }}>{tx("pro.products.simBadge")}</span>
                            )}
                          </div>
                        );
                      }}
                    />
                  ) : (
                    !telemetryLoading && (
                      <Text level="body-small" style={{ color: "#9CA3AF" }}>{tx("pro.products.noDatapointsAvailable")}</Text>
                    )
                )}
              </>
            )}
          </div>
          </>
          )}
        </CollapsibleSection>

        {/* Historical Data */}
        <CollapsibleSection
          id={SECTION_IDS.historical(productId)}
          title={t("pro.products.historicalData")}
          subtitle={historyRangeLabel ?? undefined}
          icon={<BarChart3 size={20} />}
          expanded={isExpanded(SECTION_IDS.historical(productId))}
          onToggleExpanded={() => toggle(SECTION_IDS.historical(productId))}
          statusIndicators={bluetoothOnly ? undefined : [
            <HistoricalDataIndicator
              key="historical"
              status={
                product.isSimulated && !product.isSimulating
                  ? 'simulationPaused'
                  : product.isSimulated && product.isSimulating
                    ? 'simulated'
                  : historyStatus
              }
              source={historySource}
              domain={product.domain}
            />,
          ]}
        >
          {bluetoothOnly ? (
            <div className={s.cardInner}><ComingSoonCard title={t("pro.products.bluetoothOnly.title")} message={t("pro.products.bluetoothOnly.message")} /></div>
          ) : (
          <>
          <div className={s.cardInner}>

              {product.isSimulated && !product.isSimulating ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "16px 0" }}>
                  <Text level="body-small" style={{ color: "#9CA3AF", textAlign: "center" }}>
                    Simulation is paused — start a session to view historical data.
                  </Text>
                  {canEditProduct ? (
                    <StartSimulationControl
                      token={token}
                      productId={product.id}
                      onStarted={handleSimulationChange}
                      selectedDuration={simDuration}
                      onDurationChange={setSimDuration}
                    />
                  ) : null}
                </div>
              ) : telemetryOffline && !product.isSimulated ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 0" }}>
                  <Text level="body-small" style={{ color: "#9CA3AF", textAlign: "center" }}>
                    {DEVICE_OFFLINE_MESSAGE}
                  </Text>
                </div>
              ) : (
                <ListView
                  key={`history-${productId}-${effectiveHistoryKeys.join(',')}-${historyRefreshKey}`}
                  listId={`product-history-${productId}`}
                  columns={historyColumns}
                  fetchList={fetchHistoryList}
                  enabledViews={['chart', 'table']}
                  disableLocalPersistence={true}
                  defaultPageSize={10}
                  initialColumnFilters={historyDefaultFilters}
                  contextKey={`${productId}-${historyRefreshKey}`}
                  timeRangeFilter={{ enabled: true }}
                  chartLiveRows={chartLiveRows}
                  emptyTitle={DEVICE_OFFLINE_MESSAGE}
                  emptySubtitle=""
                />
            )}
          </div>
          </>
          )}
        </CollapsibleSection>

        {/* Alerts */}
        <CollapsibleSection
          id={SECTION_IDS.alerts(productId)}
          title={t("pro.fleet.alerts.typeAlerts")}
          subtitle={
            alertCounts != null
              ? `${alertCounts.active} active & ${alertCounts.recent} recent alerts`
              : undefined
          }
          icon={<AlertTriangle size={20} />}
          expanded={isExpanded(SECTION_IDS.alerts(productId))}
          onToggleExpanded={() => toggle(SECTION_IDS.alerts(productId))}
        >
          {bluetoothOnly ? (
            <div className={s.cardInner}><ComingSoonCard title={t("pro.products.bluetoothOnly.title")} message={t("pro.products.bluetoothOnly.message")} /></div>
          ) : (
          <>
          <div className={s.cardInner}>
            {product.isSimulated && !product.isSimulating ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "16px 0" }}>
                <Text level="body-small" style={{ color: "#9CA3AF", textAlign: "center" }}>
                  Simulation is paused — start a session to view alerts.
                </Text>
                {canEditProduct ? (
                  <StartSimulationControl
                    token={token}
                    productId={product.id}
                    onStarted={handleSimulationChange}
                    selectedDuration={simDuration}
                    onDurationChange={setSimDuration}
                  />
                ) : null}
              </div>
            ) : (
              <ProductAlertsSection
                productId={productId}
                productName={product.name}
                owningOrganizationId={
                  product.organizations.find((o) => o.role === "owner")
                    ?.organizationId ?? null
                }
                token={token}
                canDismissAlerts={canEditProduct}
                offline={Boolean(telemetryOffline && !product.isSimulated)}
                onCountsChange={setAlertCounts}
                insightsEnabled={insightsEnabled}
                showInsightUsage={isSuperAdmin}
              />
            )}
          </div>
          </>
          )}
        </CollapsibleSection>
      </DetailPageLayout>
      
      {/* Modals */}
      <ComponentFormModal
        open={componentModalOpen}
        onClose={() => setComponentModalOpen(false)}
        productId={productId}
        accessToken={token ?? ""}
        existing={editingComponent}
        onSaved={(saved) => {
          setComponents((prev) => {
            const idx = prev.findIndex((c) => c.id === saved.id);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = saved;
              return next;
            }
            return [...prev, saved];
          });
        }}
      />
      <ComponentViewModal
        open={Boolean(viewingComponent)}
        onClose={() => setViewingComponent(null)}
        accessToken={token ?? ""}
        component={viewingComponent}
      />
      <RequestProductAccessModal
        open={requestModalOpen}
        setOpen={setRequestModalOpen}
        productName={product.name}
        currentTier={myStructuredTier}
        currentAccessTitle={formatProductAccessTooltip(myAccess)}
        initialTier={changeRequestInitialTier}
        pending={accessPending}
        pendingTier={accessPendingTier}
        submitting={requestingAccess}
        cancelling={cancellingAccessRequest}
        error={requestAccessError}
        onSubmit={submitAccessRequest}
        onCancelRequest={cancelAccessRequest}
      />
      <ComponentConfirmModal />
      <ProductSubscriptionsModal
        open={subscriptionsModalOpen}
        setOpen={setSubscriptionsModalOpen}
        productId={productId}
        productName={product.name}
        owningOrganizationId={product.organizations.find(o => o.role === 'owner')?.organizationId ?? null}
        initialSubscriberUserId={subscriptionsTargetUserId}
        initialSubscriberLabel={subscriptionsTargetLabel}
        lockUser={subscriptionsLockUser}
        lockProduct
        skipUserPrefill={subscriptionsSkipUserPrefill}
        summaryPerspective={
          subscriptionsSkipUserPrefill
            ? undefined
            : subscriptionsTargetUserId &&
                currentUserId &&
                subscriptionsTargetUserId !== currentUserId
              ? "named"
              : "me"
        }
        onGoToProductAccess={() => {
          const sectionId = SECTION_IDS.access(productId);
          if (!isExpanded(sectionId)) {
            toggle(sectionId);
          }
          window.setTimeout(() => {
            document.getElementById(sectionId)?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 50);
        }}
        onSaved={() => {
          setSubscribersRefreshKey((k) => k + 1);
        }}
      />
    </>
  );
}
